<template>
  <v-card>
    <v-img :src="logoSrc" :aspect-ratio="3" />
    <v-card-text>
      <v-form
        v-if="otpVerify"
        ref="otpForm"
        class="text-center"
        @submit.prevent="otpVerifyHandler"
      >
        <template v-if="mode === OtpAuthModeEnum.verification">
          <div>
            <v-otp-input
              ref="otpInput"
              v-model="verificationCode"
              label="Enter verification code"
              variant="outlined"
              hide-details="auto"
              :length="6"
              style="max-width: 400px"
              class="mx-auto"
              :rules="[v => !!v || 'Code is required']"
            />
          </div>
          <div class="d-flex justify-center">
            <v-checkbox
              v-model="rememberDevice"
              label="Remember this device for 30 days"
              hide-details
            />
          </div>
          <div style="font-size: 16px" class="my-4 text-medium-emphasis">
            Enter the code from your two-factor authenticator app.
            <br>
            If you've lost your device, you can <a href="#" @click="changeOtpAuthModeHandler($event, OtpAuthModeEnum.recovery)">enter</a> one of your recovery codes.
          </div>
        </template>
        <template v-else>
          <v-text-field
            v-model="verificationCode"
            label="Enter recovery code"
            :rules="[v => !!v || 'Code is required']"
          />
          <div style="font-size: 16px" class="my-4 text-medium-emphasis">
            <a href="#" @click="changeOtpAuthModeHandler($event, OtpAuthModeEnum.verification)">Enter</a> the code from your two-factor authenticator app.
            <br>
            If you've lost your device, you can enter one of your recovery codes.
          </div>
        </template>
        <v-btn class="primary" :loading="verificationLoading" type="submit">
          Verify Code
        </v-btn>
      </v-form>
      <ForgotPasswordRequestForm
        v-else-if="forgotPass"
        :initial-email="email.trim()"
        @click:back="forgotPass = false"
      />
      <v-form
        v-else-if="!resetPass"
        ref="signInForm"
        @submit="submit"
      >
        <v-alert
          v-if="error"
          type="warning"
          class="mb-6"
        >
          {{ authErrorMessage }}
        </v-alert>
        <v-text-field
          v-model="email"
          label="E-mail"
          :rules="emailRules"
          placeholder="Email Address"
          autocapitalize="none"
          type="email"
          :hide-details="false"
        />
        <v-text-field
          v-model="password"
          :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[rules.required, rules.min]"
          :type="showPassword ? 'text' : 'password'"
          name="input-10-1"
          label="Password"
          hint="At least 8 characters"
          counter
          placeholders="Enter your password"
          autocomplete="off"
          autocapitalize="none"
          @click:append-inner="showPassword = !showPassword"
          @keydown.enter.prevent="submit"
        />
        <div class="text-center">
          <div v-if="showCaptchaContainer" id="captcha-sign-in-container" class="captcha-container" />
          <v-btn
            class="primary mb-5"
            :loading="requestLoading"
            @click="submit"
          >
            Sign In
          </v-btn>
          <div class="d-flex flex-column align-center">
            <v-btn
              variant="text"
              color="primary"
              class="auth-link-btn"
              data-test="forgot-password-switch"
              @click="forgotPass = true"
            >
              Forgot password?
            </v-btn>
            <v-btn
              variant="text"
              color="primary"
              class="auth-link-btn"
              data-test="magic-link-switch"
              @click="enableResetPassword"
            >
              Trouble logging in? Email me a sign-in link
            </v-btn>
          </div>
        </div>
      </v-form>
      <v-form
        v-else
        ref="resetLinkForm"
        @submit.prevent="sendLink"
      >
        <div v-if="resetSent">
          <p>Please check your email for a link to sign in.</p>
          <v-btn
            variant="text"
            color="primary"
            class="auth-link-btn"
            data-test="back-to-sign-in"
            @click="resetLogin"
          >
            Back to sign in with a password
          </v-btn>
        </div>
        <div v-else>
          <p v-if="resetError" class="alert error mb-3">
            There was an error trying to send a link. It appears that
            the reset token has expired. Please try again.
          </p>
          <p v-else class="mb-3">
            Enter the email address associated with your account, and we’ll send a magic link to your inbox.
          </p>
          <v-text-field
            v-model="resetEmail"
            label="E-mail"
            :rules="emailRules"
            placeholder="Email Address"
            autocomplete="off"
          />
          <div class="text-center">
            <div v-if="showCaptchaContainer" id="captcha-reset-container" class="captcha-container mt-4" />
            <v-btn
              class="primary mb-5"
              :loading="resetPasswordLoading"
              type="submit"
            >
              Send Magic Link
            </v-btn>
            <v-spacer />
            <v-btn
              variant="text"
              color="primary"
              class="auth-link-btn"
              data-test="back-to-sign-in"
              @click="resetLogin"
            >
              Back to sign in with a password
            </v-btn>
          </div>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import { LOGIN, SET_PREVIOUS_URL, TOGGLE_SPLASH_SCREEN } from '@/store/action-types';
import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { getReturnUrlForAccount, isPentestEmail } from '@/bundles/Auth/helpers';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { renderCaptcha, resetCaptcha, validateCaptcha } from '@/plugins/captcha';
import { RootActions } from '@/store/types/action-types';
import { OtpAuthModeEnum } from '@/bundles/Auth/types';
import { emailPattern } from '@/utils/validation';
import { FORGOT_PASSWORD_STEP } from '@/bundles/Auth/consts';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import slickCactusDarkImg from '@/assets/img/Slick-Cactus-Dark.png';
import slickCactusWhiteImg from '@/assets/img/Slick-Cactus-White.svg';

import ForgotPasswordRequestForm from '@/bundles/Auth/components/ForgotPasswordRequestForm.vue';

export default {
  name: 'SignInCard',

  components: { ForgotPasswordRequestForm },

  mixins: [AccountLayoutMixin],

  setup () {
    const { addNotification } = useNotifications();
    const { isClient } = useUserRoles();

    return { addNotification, isClient };
  },

  data: (vm) => ({
    emailRules: [
      (v) => !!v || 'E-mail is required',
      (v) =>
        emailPattern.test(v) ||
        'E-mail must be valid',
    ],
    password: '',
    resetSent: false,
    resetPass: false,
    forgotPass: vm.$route?.query.step === FORGOT_PASSWORD_STEP,
    resetError: false,
    resetEmail: '',
    showPassword: false,
    email: '',
    error: false,
    rules: {
      required: (value) => !!value || 'Required.',
      min: (v) => v.length >= 6 || 'Min 6 characters',
    },
    requestLoading: false,
    resetPasswordLoading: false,
    authErrorMessage: null,
    otpVerify: false,
    verificationCode: null,
    verificationLoading: false,
    rememberDevice: false,
    mode: OtpAuthModeEnum.verification,
    showCaptchaContainer: true,
  }),

  computed: {
    captchaTheme () {
      return this.$vuetify.theme.current.dark ? 'dark' : 'light';
    },
    // The wordmark is black on white; on the dark theme's card it becomes a
    // white box, so the dark theme gets the same artwork with a white wordmark
    // on a transparent, identically framed 3:1 canvas.
    logoSrc () {
      return this.$vuetify.theme.current.dark ? slickCactusWhiteImg : slickCactusDarkImg;
    },
    ...mapState({
      token: (state) => state.idToken,
      prevUrl: (state) => state.prevUrl,
    })
  },

  watch: {
    resetPass: {
      handler: function (value) {
        this.showCaptchaContainer = true;
        if (value) {
          this.$nextTick(() => {
            renderCaptcha('captcha-reset-container', this.captchaTheme);
          });
          return;
        }

        this.renderSignInCaptcha();
      },
      immediate: true,
    },
    forgotPass (value) {
      if (!value) {
        this.showCaptchaContainer = true;
        this.renderSignInCaptcha();
      }
    },
    email: function (value) {
      this.hideCaptchaContainer(value);
    },
    resetEmail: function (value) {
      this.hideCaptchaContainer(value);
    },
    showCaptchaContainer: function (value) {
      const container = this.resetPass ? 'captcha-reset-container' : 'captcha-sign-in-container';
      if (value) {
        this.$nextTick(() => {
          try {
            renderCaptcha(container, this.captchaTheme);
          } catch (_) {}
        });
      }
    },
  },

  created () {
    this.OtpAuthModeEnum = OtpAuthModeEnum;
  },

  methods: {
    ...mapActions({
      login: LOGIN,
      setLoginInfo: RootActions.SET_LOGIN_INFO,
    }),
    ...mapMutations({
      toggleScreen: TOGGLE_SPLASH_SCREEN,
      setPreviousUrl: SET_PREVIOUS_URL,
    }),
    ...mapMutations('Notifications', ['ADD_NOTIFICATION']),
    renderSignInCaptcha () {
      if (this.forgotPass) { // ForgotPasswordRequestForm renders its own captcha
        return;
      }
      this.$nextTick(() => {
        renderCaptcha('captcha-sign-in-container', this.captchaTheme);
      });
    },
    hideCaptchaContainer: function (value) {
      const pentestUser = isPentestEmail(value);
      this.showCaptchaContainer = !(pentestUser && this.showCaptchaContainer);
    },
    handleCaptcha: function () {
      const captchaResponse = validateCaptcha();
      let captchaValidated = typeof captchaResponse === 'boolean' && captchaResponse;
      if (typeof captchaResponse === 'string' && captchaResponse) {
        return captchaResponse;
      }
      if (captchaValidated) {
        return true;
      }
      const captchaContainer = document.querySelector('.captcha-container');
      if (!captchaContainer) {
        return false;
      }
      captchaContainer.classList.add('captcha-container--error');
      captchaContainer.focus();
      return false;
    },
    async submit () {
      const { valid } = await this.$refs.signInForm.validate();
      if (!valid) {
        return;
      }
      const captchaResponse = this.handleCaptcha();
      if (typeof captchaResponse === 'boolean' && !captchaResponse && this.showCaptchaContainer) {
        return;
      }
      const authPayload = {
        email: this.email.toLowerCase().trim(),
        password: this.password,
      };
      if (typeof captchaResponse === 'string' && captchaResponse) {
        authPayload.response = captchaResponse;
      }
      this.requestLoading = true;
      try {
        const response = await this.login(authPayload);

        this.error = false;
        this.authErrorMessage = null;

        if (response?.otp_required) {
          this.otpVerify = true;
          this.$nextTick(() => {
            this.$refs.otpForm.resetValidation();
            this.$refs.otpInput.focus();
          })
          return;
        }

        await this.finishSignIn();
      } catch (error) {
        this.ADD_NOTIFICATION({ ...error });
        this.error = true;
        this.authErrorMessage = error.message;
        resetCaptcha();
      } finally {
        this.requestLoading = false;
      }
    },
    async sendLink () {
      const { valid } = await this.$refs.resetLinkForm.validate();
      if (!valid) {
        return;
      }

      this.resetPasswordLoading = true;
      try {
        const captchaResponse = this.handleCaptcha();
        if (typeof captchaResponse === 'boolean' && !captchaResponse && this.showCaptchaContainer) {
          return;
        }
        const resetPasswordPayload = { email: this.resetEmail.toLowerCase().trim() };
        if (typeof captchaResponse === 'string' && captchaResponse) {
          resetPasswordPayload.response = captchaResponse;
        }
        await authService.magicLinkRequest(resetPasswordPayload);

        this.resetSent = true;
      } catch (error) {
        const notification = { ...error, status: 0 };
        this.ADD_NOTIFICATION(notification);
        resetCaptcha();
      } finally {
        this.resetPasswordLoading = false;
      }
    },
    resetLogin () {
      this.resetPass = false;
      this.resetSent = false;
    },
    enableResetPassword () {
      // the email already typed on the password form carries over to the sign-in link form
      if (this.email.trim()) {
        this.resetEmail = this.email.trim();
      }
      this.resetPass = true;
    },
    async otpVerifyHandler () {
      try {
        const { valid } = await this.$refs.otpForm.validate();
        if (!valid) {
          return;
        }

        this.verificationLoading = true;

        const payload = {
          code_verifier: this.verificationCode,
          with_login: true,
          email: this.email.toLowerCase().trim(),
          password: this.password,
          remember_device: this.rememberDevice,
        };

          const response = await authService.otpVerify(payload);

        await this.setLoginInfo(response);
        await this.finishSignIn();
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
        this.verificationCode = null;
        this.$refs.otpInput?.focus();
      } finally {
        this.verificationLoading = false;
      }
    },
    // Runs once the store knows the signed-in user. The redirect used to sit in a watcher on
    // activeAccountId, which never fired when the account was the one persisted from the last
    // session, so the sign-in page stayed open after a successful login.
    async finishSignIn () {
      // several accounts: App.vue sends the user to my-accounts to pick one
      if (!this.token) {
        return;
      }

      const redirectTarget = getReturnUrlForAccount(
        this.prevUrl || this.$route.query.redirect || null,
        this.activeAccountId,
      );

      this.setPreviousUrl(null);

      const routeName = this.isClient ? 'client-occupiers-list' : 'dashboard';
      await this.$router.push(
        redirectTarget && redirectTarget !== '/dashboard'
          ? redirectTarget
          : this.getAccountRoute({ name: routeName }),
      );
      // The splash goes up only once the push has settled: it unmounts the router-view, and
      // Vue Router 4 would run the leaving page's guards with a null `this`, so the push
      // threw and the reload below never ran, leaving the splash up for good.
      this.toggleScreen(true);
      // the app loads the account settings on boot only, so every landing reloads
      window.location.reload();
    },
    changeOtpAuthModeHandler (e, mode) {
      e.preventDefault();
      this.verificationCode = '';
      this.mode = mode;
    }
  },
};
</script>
